import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiBaseUrl } from '../config/api';
import './Registration.css';

async function read(path, options) {
  const response = await fetch(`${apiBaseUrl}${path}`, {
    headers: { 'Content-Type': 'application/json' },
    ...options,
  });
  let body = {};
  try { body = await response.json(); } catch { body = {}; }
  if (!response.ok) {
    const error = new Error(typeof body.detail === 'string' ? body.detail : 'Die Anmeldung konnte gerade nicht verarbeitet werden. Bitte versuche es später erneut.');
    error.status = response.status;
    throw error;
  }
  return body;
}

function when(event) {
  if (!event?.start_at) return '';
  const start = new Date(event.start_at);
  const date = start.toLocaleDateString('de-CH', { day: '2-digit', month: 'long', year: 'numeric' });
  const time = start.toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' });
  if (!event.end_at) return `${date} · ${time}`;
  const end = new Date(event.end_at).toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' });
  return `${date} · ${time} – ${end}`;
}

function Ticket({ data, managePath }) {
  const event = data.event || {};
  const location = (event.location || '').split('\n').filter(Boolean);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [cancelAsk, setCancelAsk] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [current, setCurrent] = useState(data);
  const past = Boolean(event.past);
  const cancelledEvent = event.status === 'cancelled';
  const cancelled = current.status === 'cancelled';
  async function cancel() {
    if (!managePath || busy) return;
    setBusy(true);
    setMessage('');
    try {
      const next = await read(`/api/registrations/manage/${managePath}/cancel/`, { method: 'POST', body: '{}' });
      setCurrent(next);
      setCancelAsk(false);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <article className="ticket">
      <header className="ticket-head">
        <p className="ticket-brand">MOVELAND</p>
        <p className="ticket-kicker">{cancelled ? 'Anmeldung storniert' : cancelledEvent ? 'Event abgesagt' : past ? 'Event vorbei' : 'Dein Moveland Ticket'}</p>
      </header>
      <div className="ticket-body">
        <h2>{event.title}</h2>
        <p>{when(event)}</p>
        {location.map((line) => <p key={line}>{line}</p>)}
        {event.difficulty_label && <p>{event.difficulty_label}</p>}
        {event.arrival_note && <p>{event.arrival_note}</p>}
        {event.refreshments && <p>Verpflegung: {event.refreshments}</p>}
        {Array.isArray(event.bring_items) && event.bring_items.length > 0 && <p>Mitbringen: {event.bring_items.join(', ')}</p>}
        {event.participant_notes && <p>{event.participant_notes}</p>}
        {event.confirmation_note && <p>{event.confirmation_note}</p>}
        <div className="ticket-rule" />
        <p>{current.name}</p>
        <p>{current.participants} {current.participants === 1 ? 'Platz' : 'Plätze'}</p>
        {current.ticket_number && <p>Ticket {current.ticket_number}</p>}
        <p>Status: {current.status_label}</p>
        {message && <p>{message}</p>}
        <div className="ticket-actions">
          {event.calendar_offered && current.status === 'confirmed' && !cancelledEvent && (
            <button className="btn btn-primary" type="button" onClick={() => setCalendarOpen((open) => !open)}>Zum Kalender hinzufügen</button>
          )}
          {calendarOpen && (
            <div className="ticket-menu">
              {current.google_url && <a href={current.google_url} target="_blank" rel="noopener noreferrer">Google Calendar</a>}
              {current.outlook_url && <a href={current.outlook_url} target="_blank" rel="noopener noreferrer">Outlook</a>}
              {managePath && <a href={`${apiBaseUrl}/api/registrations/manage/${managePath}/calendar.ics`}>Apple / Kalenderdatei</a>}
            </div>
          )}
          {managePath && current.status !== 'cancelled' && !cancelAsk && (
            <button className="btn" type="button" onClick={() => setCancelAsk(true)}>Anmeldung stornieren</button>
          )}
          {cancelAsk && (
            <div className="ticket-menu">
              <p>Anmeldung stornieren? Du gibst damit {current.participants} {current.participants === 1 ? 'Platz' : 'Plätze'} für andere Teilnehmende frei.</p>
              <button className="btn" type="button" onClick={() => setCancelAsk(false)}>Zurück</button>
              <button className="btn btn-primary" type="button" disabled={busy} onClick={() => { void cancel(); }}>Anmeldung stornieren</button>
            </div>
          )}
          {!managePath && current.manage_url && <a className="btn btn-primary" href={current.manage_url}>Anmeldung ansehen</a>}
        </div>
      </div>
    </article>
  );
}

export function ConfirmRegistration() {
  const { token = '' } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    let gone = false;
    read(`/api/registrations/confirm/${token}/`, { method: 'POST', body: '{}' })
      .then((body) => { if (!gone) setData(body); })
      .catch((reason) => { if (!gone) setError(reason.message); });
    return () => { gone = true; };
  }, [token]);
  return (
    <section className="ticket-page">
      {error && <p className="ticket-note">{error}</p>}
      {data?.detail && <p className="ticket-note">{data.detail}</p>}
      {data && <Ticket data={data} />}
      <p className="ticket-note"><Link to="/events">Zu den Events</Link></p>
    </section>
  );
}

export function ManageRegistration() {
  const { token = '' } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    let gone = false;
    read(`/api/registrations/manage/${token}/`)
      .then((body) => { if (!gone) setData(body); })
      .catch((reason) => { if (!gone) setError(reason.message); });
    return () => { gone = true; };
  }, [token]);
  return (
    <section className="ticket-page">
      <p className="ticket-brand">Deine Anmeldung</p>
      {error && <p className="ticket-note">{error}</p>}
      {data && <Ticket data={data} managePath={token} />}
    </section>
  );
}
